<template>
  <v-container>
    <LoginRequired/>
    <Scanner/>
    <v-dialog
        v-model="occupiedConfirmDialog"
        width="90vw"
        max-width="90vw"
        @keydown.esc="confirmOccupiedDialog(false)"
    >
      <v-card>
        <v-toolbar dark color="warning" dense flat>
          <v-toolbar-title class="white--text">Warnung</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-4">
          Auf dem gewählten Ziellagerplatz liegen bereits Artikel. Wenn Sie fortfahren, werden die Artikel vom Quell-Lagerplatz zusätzlich auf diesen Ziellagerplatz gebucht.
        </v-card-text>
        <v-card-actions class="pt-0 px-4 pb-3 justify-space-between">
          <v-btn
              color="primary darken-1"
              text
              large
              min-width="120"
              @click.native="confirmOccupiedDialog(true)"
          >
            OK
          </v-btn>
          <v-btn
              color="secondary"
              text
              large
              min-width="120"
              @click.native="confirmOccupiedDialog(false)"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ErrorDialog
        :show="isError"
        :errorContent="errorContent"
    />

    <template v-if="isLoaded">
      <v-alert
          color="primary"
          elevation="24"
          icon="mdi-information-outline"
          type="info"
      >
        Von Lager {{ warehouseFrom }}: {{ fromBinLocationDisplay }} | Nach Lager {{ warehouseTo }}: {{ selectedToBinLocation ? selectedToBinLocation.displayName : '...' }}
      </v-alert>

      <v-container class="palette-information-container">
        <v-alert
            color="primary"
            elevation="24"
            outlined
            v-model="showStartInfo"
            @click="toggleInfo"
        >
          <v-icon color="primary">mdi-information-outline</v-icon>
          <p>Scannen oder wählen Sie den Ziel-Lagerplatz aus Lager {{ warehouseTo }} für die komplette Umlagerung vom Quell-Lagerplatz {{ fromBinLocationDisplay }}.</p>
        </v-alert>
        <v-layout justify-center>
          <v-alert
              color="primary"
              elevation="24"
              outlined
              width="60px"
              v-model="showIconOnly"
              @click="toggleInfo"
          >
            <v-icon color="primary">mdi-information-outline</v-icon>
          </v-alert>
        </v-layout>
      </v-container>

      <v-container class="text-align-center">
        <p>{{ selectedToBinLocation ? selectedToBinLocation.displayName : 'Ziel-Lagerplatz / Palette wählen' }}</p>
        <v-text-field
            :value="selectedToBinLocation ? selectedToBinLocation.code : ''"
            label="Ziel-Lagerplatz / Palette scannen"
            outlined
            readonly
            dense
            ref="scannerResult"
            style="margin: auto"
            prepend-icon="mdi-barcode-scan"
        ></v-text-field>
        <v-btn
            color="success"
            class="ma-2 white--text"
            :disabled="!selectedToBinLocation"
            @click="proceedToConfirm"
        >
          Weiter
        </v-btn>
      </v-container>

      <div v-if="binLocations && binLocations.length > 0">
        <v-text-field
            v-model="search"
            dense
            clearable
            outlined
            prepend-icon="mdi-magnify"
            label="Suche nach Code oder Name"
        ></v-text-field>
        <v-virtual-scroll
            v-if="filteredBinLocations && filteredBinLocations.length > 0"
            :items="filteredBinLocations"
            :item-height="56"
            :height="360"
            class="palette-scroll"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.code" class="palette-list-item">
              <v-btn
                  @click="selectToBinLocation(item)"
                  block
                  color="secondary"
                  class="white--text palette-button"
              >
                {{ item.displayName }}
              </v-btn>
            </v-list-item>
          </template>
        </v-virtual-scroll>
        <v-alert
            v-else
            dense
            outlined
            type="info"
        >
          Keine Treffer für die aktuelle Suche.
        </v-alert>
      </div>
      <div v-else>
        <v-alert
            dense
            outlined
            type="error"
        >
          Keine Ziellagerplätze im {{ warehouseTo }} gefunden!
        </v-alert>
      </div>
    </template>

    <v-container v-else>
      <v-progress-circular
          indeterminate
          color="primary"
      ></v-progress-circular>
    </v-container>

    <VerticalSpacer/>
    <v-row>
      <v-spacer/>
      <v-btn
          :to="{ name: 'rearrange.entire_bin_location.select_bin_location_from', params: { from: warehouseFrom, to: warehouseTo } }"
          outlined
          color="primary"
      >
        Zurück
      </v-btn>
      <v-spacer/>
    </v-row>
  </v-container>
</template>

<script>
import VerticalSpacer from "../../Others/VerticalSpacer";
import LoginRequired from "../../Others/LoginRequired";
import Scanner from "../../Others/Scanner";
import ErrorDialog from "../../Others/ErrorDialog";

export default {
  components: {ErrorDialog, Scanner, LoginRequired, VerticalSpacer},
  data: () => ({
    showStartInfo: false,
    showIconOnly: true,
    selectedToBinLocation: null,
    warehouseFrom: '',
    warehouseTo: '',
    fromBinLocation: '',
    fromBinLocationDisplay: '',
    isLoaded: false,
    isError: false,
    errorContent: '',
    binLocations: [],
    search: '',
    occupiedConfirmDialog: false,
    occupiedConfirmResolver: null,
  }),
  computed: {
    filteredBinLocations() {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) {
        return this.binLocations;
      }

      return this.binLocations.filter(binLocation => {
        const code = (binLocation.code || '').toLowerCase();
        const displayName = (binLocation.displayName || '').toLowerCase();
        return code.includes(query) || displayName.includes(query);
      });
    },
  },
  created() {
    this.$root.$on('onScannerInput', this.onScannerInputHandler);
    this.$root.$on('onCloseErrorDialog', this.handleCloseErrorDialog);

    this.warehouseFrom = this.$route.params.from;
    this.warehouseTo = this.$route.params.to;
    this.fromBinLocation = this.$route.params.fromBinLocation;
    this.fromBinLocationDisplay = this.$route.query.fromDisplayName || this.fromBinLocation;

    this.loadData();
  },

  beforeDestroy() {
    this.$root.$off('onScannerInput', this.onScannerInputHandler);
    this.$root.$off('onCloseErrorDialog', this.handleCloseErrorDialog);
    if (this.occupiedConfirmResolver) {
      this.occupiedConfirmResolver(false);
      this.occupiedConfirmResolver = null;
    }
  },

  methods: {
    async loadData() {
      this.isLoaded = false;
      try {
        if (!this.warehouseFrom || !this.warehouseTo || !this.fromBinLocation) {
          throw new Error('Ungültige Lagerauswahl in der URL.');
        }

        this.binLocations = this.sortBinLocationsByDisplayName(await this.loadAllBinLocations(this.warehouseTo));
      } catch (error) {
        this.setScannerError(error && error.message ? error.message : 'Unbekannter Fehler');
      } finally {
        this.isLoaded = true;
      }
    },
    async loadAllBinLocations(warehouseCode) {
      const limit = 1000;
      let offset = 0;
      let total = 0;
      let loaded = 0;
      const items = [];
      const excludeSourceCode = this.shouldPreventSameLocationSelection()
          ? `&excludeCode=${encodeURIComponent(this.fromBinLocation)}`
          : '';

      do {
        const response = await this.$axios.get(
            `binLocationSelection?warehouseCode=${warehouseCode}&includePalettes=1&onlyWithStock=0${excludeSourceCode}&limit=${limit}&offset=${offset}`,
        );
        if (!(response && response.data && response.data.success)) {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Ziellagerplätze konnten nicht geladen werden.');
        }

        const chunk = Array.isArray(response.data.data) ? response.data.data : [];
        total = Number(response.data.total || 0);
        if (chunk.length === 0) {
          break;
        }
        loaded += chunk.length;
        offset += chunk.length;
        items.push(...chunk);
      } while (loaded < total);

      return items;
    },
    sortBinLocationsByDisplayName(binLocations) {
      return [...binLocations].sort((left, right) => {
        const leftLabel = (left && (left.displayName || left.code)) ? String(left.displayName || left.code) : '';
        const rightLabel = (right && (right.displayName || right.code)) ? String(right.displayName || right.code) : '';
        return leftLabel.localeCompare(rightLabel, 'de', {numeric: true, sensitivity: 'base'});
      });
    },
    toggleInfo() {
      this.showStartInfo = !this.showStartInfo;
      this.showIconOnly = !this.showIconOnly;
    },
    async selectToBinLocation(binLocation) {
      if (!binLocation) {
        this.selectedToBinLocation = null;
        return;
      }

      if (this.isNotAllowedSameAsSource(binLocation.code)) {
        return this.setScannerError('Quell- und Ziellagerplatz dürfen nicht identisch sein.');
      }

      const confirmed = await this.confirmOccupiedTargetIfNeeded(binLocation);
      if (!confirmed) {
        return;
      }

      this.isError = false;
      this.selectedToBinLocation = binLocation;
    },
    shouldPreventSameLocationSelection() {
      return this.warehouseFrom === this.warehouseTo;
    },
    isNotAllowedSameAsSource(code) {
      return this.shouldPreventSameLocationSelection() && code === this.fromBinLocation;
    },
    targetIsOccupied(binLocation) {
      if (!binLocation) {
        return false;
      }
      return !binLocation.isEmpty;
    },
    async confirmOccupiedTargetIfNeeded(binLocation) {
      if (!this.targetIsOccupied(binLocation)) {
        return true;
      }

      return this.openOccupiedConfirmDialog();
    },
    openOccupiedConfirmDialog() {
      this.occupiedConfirmDialog = true;
      return new Promise(resolve => {
        this.occupiedConfirmResolver = resolve;
      });
    },
    confirmOccupiedDialog(confirmed) {
      if (this.occupiedConfirmResolver) {
        this.occupiedConfirmResolver(confirmed);
        this.occupiedConfirmResolver = null;
      }
      this.occupiedConfirmDialog = false;
    },
    async proceedToConfirm() {
      if (!this.selectedToBinLocation) {
        return;
      }

      if (this.isNotAllowedSameAsSource(this.selectedToBinLocation.code)) {
        return this.setScannerError('Quell- und Ziellagerplatz dürfen nicht identisch sein.');
      }

      this.$router.push({
        name: 'rearrange.entire_bin_location.confirm',
        params: {
          from: this.warehouseFrom,
          to: this.warehouseTo,
          fromBinLocation: this.fromBinLocation,
          toBinLocation: this.selectedToBinLocation.code,
        },
      });
    },
    async onScannerInputHandler(scannerInputRaw) {
      if (!this.isLoaded) {
        return;
      }
      const scannerInput = this.parseJson(scannerInputRaw);
      const scannedInput = (scannerInput.dataAsText || '').replace('\n', '').trim();
      const scannedCode = scannedInput.includes('$') ? scannedInput.split('$')[1] : scannedInput;

      if (!scannedCode) {
        this.selectToBinLocation(null);
        this.blurScannerField();
        return this.setScannerError(`${scannerInput.dataAsText} ist keine gültige Eingabe für Lagerplatz!`);
      }

      if (this.isNotAllowedSameAsSource(scannedCode)) {
        this.selectToBinLocation(null);
        this.blurScannerField();
        return this.setScannerError('Quell- und Ziellagerplatz dürfen nicht identisch sein.');
      }

      const targetBinLocation = this.binLocations.find(binLocation => binLocation.code === scannedCode);
      if (!targetBinLocation) {
        this.selectToBinLocation(null);
        this.blurScannerField();
        return this.setScannerError(`Lagerplatz ${scannedCode} kann nicht gefunden werden!`);
      }

      const confirmed = await this.confirmOccupiedTargetIfNeeded(targetBinLocation);
      if (!confirmed) {
        this.blurScannerField();
        return;
      }

      this.selectedToBinLocation = targetBinLocation;
      this.isError = false;
      this.blurScannerField();
      await this.proceedToConfirm();
    },
    handleCloseErrorDialog() {
      this.isError = false;
    },
    setScannerError(message) {
      this.isError = true;
      this.errorContent = message;
    },
    parseJson(jsonString) {
      let jsonObj = {
        dataAsText: 'none',
      };
      try {
        jsonObj = JSON.parse(jsonString);
      } catch (e) {
        console.error(e);
      }
      return jsonObj;
    },
    blurScannerField() {
      if (this.$refs.scannerResult && this.$refs.scannerResult.blur) {
        this.$refs.scannerResult.blur();
      }
    },
  },
}
</script>
